/**
 * Password Strength Indicator Component
 * Provides real-time visual feedback on password strength
 * and displays validation error messages
 */

import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  validatePassword,
  getPasswordRequirements,
  getStrengthColor,
  getStrengthLabel
} from '../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
  compact?: boolean;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showRequirements = true,
  compact = false
}) => {
  const validation = validatePassword(password);

  // Don't show anything if password is empty
  if (!password) {
    return null;
  }

  const strengthColor = getStrengthColor(validation.strength);
  const strengthLabel = getStrengthLabel(validation.strength);

  // Map strength to progress value
  const strengthValue = {
    'weak': 33,
    'medium': 66,
    'strong': 100
  }[validation.strength];

  // Map strength to MUI color
  const progressColor = {
    'weak': 'error',
    'medium': 'warning',
    'strong': 'success'
  }[validation.strength] as 'error' | 'warning' | 'success';

  if (compact) {
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ mr: 1 }}>
            Password Strength:
          </Typography>
          <Typography
            variant="caption"
            color={strengthColor}
            sx={{ fontWeight: 'bold' }}
          >
            {strengthLabel}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={strengthValue}
          color={progressColor}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* Strength indicator */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Password Strength:
          </Typography>
          <Typography
            variant="body2"
            color={strengthColor}
            sx={{ fontWeight: 'bold' }}
          >
            {strengthLabel}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={strengthValue}
          color={progressColor}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Validation errors */}
      {!validation.isValid && validation.errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Password requirements not met:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>
                <Typography variant="body2">{error}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Requirements checklist */}
      {showRequirements && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Password Requirements:
          </Typography>
          <List dense disablePadding>
            {getPasswordRequirements().map((requirement, index) => {
              // Determine if this requirement is met based on errors
              const isMet = !validation.errors.some(error =>
                error.toLowerCase().includes(requirement.toLowerCase().split(' ')[0]) ||
                error.toLowerCase().includes(requirement.toLowerCase().split(' ').slice(0, 3).join(' '))
              );

              return (
                <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {isMet ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={requirement}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: isMet ? 'text.primary' : 'text.secondary'
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};
